import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import cssModules from 'react-css-modules'
import styles from './crowdfunding_share.less'
import { getI18n } from 'client/i18n'
import StandardError from 'components/common/standard_error'
import CrossFadeTransition from 'components/common/transitions/cross_fade'
import {
  CIP_ELIGIBILITY_CODES,
  COUNTRY_NOT_SUPPORTED,
  QUALTRICS_GN_CREATION_FEEDBACKLINK,
  QUALTRICS_GNC_CREATION_FEEDBACKLINK,
  UNDER_REVIEW_ERROR,
} from 'shared/shared_consts'
import { NEW_CAMPAIGN_ID } from 'redux/modules/campaign'
import { sendEnsightenEvent } from 'lib/ensighten_analytics'
import ShareCampaignTextarea from 'components/common/share_campaign_textarea'
import { Button, HeadingText, Link } from '@paypalcorp/pp-react'
import CampaignEligibility from 'components/common/campaign_eligibility'
import { dexterURL, decodeCampaignId } from 'shared/campaign_url'
import { trackImpression, trackEventNameClick } from 'lib/fpti_analytics'
import PassiveFeedbackLink from 'components/passive_feedback_link'

export const I18N_CONTEXT = 'components/pages/crowdfunding/pool_share'
const i18n = getI18n(I18N_CONTEXT)

class CrowdfundingShare extends React.Component {
  static propTypes = {
    onPublish: PropTypes.func.isRequired,
    operatingRemotely: PropTypes.bool,
    campaignId: PropTypes.string,
    poolUrl: PropTypes.string.isRequired,
    creationError: PropTypes.object,
    shareDescription: PropTypes.string,
    params: PropTypes.object,
    charity: PropTypes.object,
    paypalDomain: PropTypes.string,
    route: PropTypes.object,
    enable_ces_link: PropTypes.bool,
  }

  campaignCreated = ({ campaignId }) => {
    return campaignId !== NEW_CAMPAIGN_ID
  }

  componentDidMount() {
    const { operatingRemotely, creationError } = this.props

    // Checking if the campaign has already been created so that we don't create it twice.
    // This can happen when going back from the share page to here.
    if (
      !operatingRemotely &&
      !creationError &&
      !this.campaignCreated(this.props)
    ) {
      this.props.onPublish()
    }
  }

  UNSAFE_componentWillReceiveProps = newProps => {
    if (this.campaignCreated(newProps) && !this.campaignCreated(this.props)) {
      sendEnsightenEvent('MoneyPoolFin')
    }
  }

  render() {
    let Component
    const {
      creationError,
      charity,
      enable_ces_link,
      paypalDomain,
      route,
    } = this.props
    const hideLogo = get(this.props, 'params.hideLogo', false)
    const hideButton = get(this.props, 'params.hideButton', false)
    const isPublishPath = route && route.path === 'publish'
    const isShowingCESLink = isPublishPath && enable_ces_link

    if (creationError) {
      if (!CIP_ELIGIBILITY_CODES.hasOwnProperty(creationError.error)) {
        trackImpression({
          error_desc: creationError.error || 'CREATE_ERROR',
          error_code: 500,
          event_type: 'ac',
          flow: charity ? 'GENEROSITY_NETWORK_CHARITIES' : 'GENEROSITY_NETWORK',
          event_name: 'dw_giving_failed_customer_interaction_occurred',
          screen: 'publish',
        })
      }

      if (creationError.error === UNDER_REVIEW_ERROR) {
        Component = (
          <StandardError
            key="review_error"
            iconType="error"
            title={i18n('review_error_title')}
            subtitle={i18n('review_error_subtitle')}
            backButton={true}
          />
        )
      } else if (CIP_ELIGIBILITY_CODES.hasOwnProperty(creationError.error)) {
        if (CIP_ELIGIBILITY_CODES[creationError.error] === 'NEED_MORE_DATA') {
          const returnUrl = '/pools/c/create-crowdfunding/basics'
          location.href = dexterURL(returnUrl)
          return
        } else {
          Component = (
            <CampaignEligibility
              CIPStatusCode={CIP_ELIGIBILITY_CODES[creationError.error]}
            />
          )
        }
      } else if (creationError.error === COUNTRY_NOT_SUPPORTED) {
        Component = (
          <StandardError
            key="error"
            iconType="warning"
            title={i18n('error_country_eligible_title')}
            subtitle={i18n('error_country_eligible_sub_title')}
            extraContent={
              <Button
                as="a"
                href={`https://${paypalDomain}/myaccount/summary`}
                role="button"
                secondary
              >
                {i18n('error_country_eligible_button')}
              </Button>
            }
          />
        )
      } else {
        Component = (
          <StandardError
            key="error"
            iconType="warning"
            title={i18n('error_title')}
            subtitle={i18n('error_subtitle')}
            onTryAgain={this.props.onPublish}
          />
        )
      }
    } else if (this.campaignCreated(this.props)) {
      if (isPublishPath) {
        trackImpression({
          event_name: 'dw_giving_fundraiser_publish_screen_shown',
          charity_name: charity && charity.name,
          flow:
            charity && charity.name
              ? 'GENEROSITY_NETWORK_CHARITIES'
              : 'GENEROSITY_NETWORK',
          npid: charity && charity.nonprofit_id,
          campaignId: decodeCampaignId(this.props.campaignId),
        })
      }

      Component = (
        <div>
          {!hideLogo && <div styleName="success" />}
          <HeadingText size="md" styleName="title">
            {i18n('share_title')}
          </HeadingText>
          <div styleName="subtitle-wrapper">
            <div styleName="subtitle">{i18n('share_your_page')}</div>
          </div>
          <div styleName="textarea-wrapper">
            <ShareCampaignTextarea
              campaignId={this.props.campaignId}
              description={this.props.shareDescription}
              data-pa-click="share"
              data-nemo="share-pool"
            />
          </div>
          {!hideButton && (
            <Link
              data-pa-click="goToPool"
              data-nemo="go-to-pool"
              href={this.props.poolUrl}
              className={'vx_btn vx_btn-block vx_btn-secondary'}
              onClick={() =>
                trackEventNameClick({
                  event_name: 'dw_giving_fundraiser_publish_view_pressed',
                  charity_name: charity && charity.name,
                  npid: charity && charity.nonprofit_id,
                  campaignId: decodeCampaignId(this.props.campaignId),
                  flow:
                    charity && charity.name
                      ? 'GENEROSITY_NETWORK_CHARITIES'
                      : 'GENEROSITY_NETWORK',
                })
              }
            >
              {i18n('see_my_story')}
            </Link>
          )}
          {isShowingCESLink && (
            <PassiveFeedbackLink
              // eslint-disable-next-line no-undef
              reloadQualtrics={PAYPAL.analytics.reloadQualtrics}
              uniqueFeedbackName={
                charity && charity.nonprofit_id
                  ? QUALTRICS_GNC_CREATION_FEEDBACKLINK
                  : QUALTRICS_GN_CREATION_FEEDBACKLINK
              }
            />
          )}
        </div>
      )
    } else {
      // assume operatingRemotely
      trackImpression({
        pageName: 'create:spinner',
        flow:
          charity && charity.name
            ? 'GENEROSITY_NETWORK_CHARITIES'
            : 'GENEROSITY_NETWORK',
      })
      Component = (
        <div styleName="create-indicator-container" key="spinner">
          <div className="vx_spinner-large" styleName="create-indicator" />
          <div styleName="spinner-title">{i18n('creating')}</div>
        </div>
      )
    }

    return <CrossFadeTransition>{Component}</CrossFadeTransition>
  }
}

export default cssModules(CrowdfundingShare, styles)
